import React, { useEffect, useMemo } from "react";
import { Box, Tooltip } from "@mui/material";
import { Droppable } from "@hello-pangea/dnd";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
  ExpandMoreOutlined,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { fetchTeams, fetchTeamWorkload } from "../../store/workOrdersSlice";
import { RootState } from "../../../../core/store";
import { TeamMemberWorkload } from "../../models/models";
import SidebarTeamRow, { getTeamColor } from "./SidebarTeamRow";
import SidebarMemberRow from "./SidebarMemberRow";
import { getWorkloadColor } from "./SidebarMemberRow";
import { getInitials } from "../shared/AssigneeAvatarStack";

interface TeamSidebarProps {
  collapsed: boolean;
  sectionCollapsed: boolean;
  onToggleSectionCollapse: () => void;
  onToggleCollapse: () => void;
  onOpenTeamManagement: () => void;
  canManage: boolean;
}

const TeamSidebar: React.FC<TeamSidebarProps> = ({
  collapsed,
  sectionCollapsed,
  onToggleSectionCollapse,
  onToggleCollapse,
  onOpenTeamManagement,
  canManage,
}) => {
  const { translate } = useTranslation("operations");
  const dispatch = useAppDispatch();
  const { teams, teamsLoading, teamWorkload } = useAppSelector(
    (state: RootState) => state.workOrders
  );

  // Fetch teams on mount
  useEffect(() => {
    if (teams.length === 0 && !teamsLoading) {
      dispatch(fetchTeams());
    }
  }, [dispatch, teams.length, teamsLoading]);

  // Fetch workload for each team (the API is per-team)
  useEffect(() => {
    teams.forEach((team) => {
      dispatch(fetchTeamWorkload(team.id));
    });
  }, [dispatch, teams]);

  // Build workload map: teamId -> TeamMemberWorkload[]
  // Using the current teamWorkload from the store (last fetched team)
  // For the sidebar, we need the workload per team. Since the store only holds one
  // at a time, we'll use the team.members with their openWorkOrderCount from the
  // team list API (which includes members with counts when expanded).
  // The sidebar will use team.members data directly.
  const teamWorkloadMap = useMemo(() => {
    const map: Record<string, TeamMemberWorkload[]> = {};
    teams.forEach((team) => {
      if (team.members && team.members.length > 0) {
        map[team.id] = team.members.map((m) => ({
          userId: m.userId,
          userName: m.userName,
          openCount: m.openWorkOrderCount || 0,
          overdueCount: 0,
          completedCount: 0,
        }));
      } else {
        map[team.id] = [];
      }
    });
    return map;
  }, [teams]);

  // Collect users who are NOT in any team (for "Individuals" section)
  // We don't have a separate "all users" endpoint here. Individuals appear
  // when team members are known. For now, individuals = empty unless
  // the team API provides unteamed users. This can be enhanced later.

  // Aggregate workload for collapsed dot color per team
  const getAggregateWorkloadColor = (members: TeamMemberWorkload[]): string => {
    if (members.length === 0) return "#10B981";
    const maxOpen = Math.max(...members.map((m) => m.openCount));
    return getWorkloadColor(maxOpen);
  };

  // ─── Collapsed sidebar ─────────────────────────────────────
  if (collapsed) {
    return (
      <Box
        sx={{
          width: "100%",
          backgroundColor: dt.cardBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
          overflow: "hidden",
          paddingTop: "8px",
          gap: "4px",
        }}
      >
        {/* Expand button */}
        <Tooltip title={translate("sidebar.expand")} placement="right">
          <Box
            onClick={onToggleCollapse}
            sx={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: dt.rowHover },
              marginBottom: "8px",
            }}
          >
            <ChevronRightOutlined sx={{ fontSize: 18, color: dt.textSecondary }} />
          </Box>
        </Tooltip>

        {/* Stacked team avatars with workload dots */}
        {teams.map((team) => {
          const members = teamWorkloadMap[team.id] || [];
          const dotColor = getAggregateWorkloadColor(members);
          const teamColor = getTeamColor(team.id);

          return (
            <Tooltip key={team.id} title={team.name} placement="right">
              <Box
                onClick={onToggleCollapse}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "3px",
                  cursor: "pointer",
                  padding: "6px 0",
                }}
              >
                {/* Team circle with initials */}
                <Box
                  sx={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: teamColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: 700,
                    fontFamily: dt.font,
                    color: "#FFFFFF",
                  }}
                >
                  {getInitials(team.name)}
                </Box>
                {/* Workload dot */}
                <Box
                  sx={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor: dotColor,
                  }}
                />
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  }

  // ─── Section collapsed (within expanded sidebar) ────────────
  if (sectionCollapsed) {
    return (
      <Box
        onClick={onToggleSectionCollapse}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px", /* spec: section horizontal + vertical padding */
          cursor: "pointer",
          transition: "background-color 0.15s ease",
          flexShrink: 0,
          "&:hover": { backgroundColor: "#F7F5F0" /* spec: input-bg hover */ },
        }}
      >
        <Box
          sx={{
            fontFamily: dt.fontMono, /* spec: JetBrains Mono */
            fontSize: "11px",
            fontWeight: 600,
            color: "#8C9099", /* spec: text-muted */
            textTransform: "uppercase",
            letterSpacing: "0.1em", /* spec: section header */
          }}
        >
          {translate("sidebar.title")}
        </Box>
        <ExpandMoreOutlined sx={{ fontSize: 14, color: "#8C9099" /* spec: text-muted */, flexShrink: 0 }} />
      </Box>
    );
  }

  // ─── Expanded sidebar ───────────────────────────────────────
  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: dt.cardBg,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
      }}
    >
      {/* Header — spec: section header style */}
      <Box
        onClick={onToggleSectionCollapse}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px", /* spec: section horizontal + vertical padding */
          flexShrink: 0,
          cursor: "pointer",
          "&:hover": { backgroundColor: "#F7F5F0" /* spec: input-bg hover */ },
          transition: "background-color 0.15s ease",
        }}
      >
        <Box
          sx={{
            fontFamily: dt.fontMono, /* spec: JetBrains Mono */
            fontSize: "11px",
            fontWeight: 600,
            color: "#8C9099", /* spec: text-muted */
            textTransform: "uppercase",
            letterSpacing: "0.1em", /* spec: section header */
          }}
        >
          {translate("sidebar.title")}
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {/* Manage teams (+) icon — Admin only */}
          {canManage && (
            <Tooltip title={translate("sidebar.manageTeams")}>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenTeamManagement();
                }}
                sx={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                  color: "#8C9099", /* spec: text-muted */
                  fontSize: "16px",
                  fontWeight: 300,
                  "&:hover": { backgroundColor: "#F7F5F0" /* spec: input-bg */ },
                }}
              >
                +
              </Box>
            </Tooltip>
          )}

          {/* Sidebar collapse chevron */}
          <Tooltip title={translate("sidebar.collapse")}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
              sx={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                "&:hover": { backgroundColor: "#F7F5F0" /* spec: input-bg */ },
              }}
            >
              <ChevronLeftOutlined sx={{ fontSize: 14, color: "#8C9099" /* spec: text-muted */ }} />
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Team sections — wrapped in Droppable for DnD source */}
      <Box sx={{ padding: "0 16px" /* spec: section horizontal padding */ }}>
        {teams.length === 0 ? (
          <Box
            sx={{
              padding: "24px 0",
              fontFamily: dt.font,
              fontSize: "12px",
              color: "#8C9099", /* spec: text-muted */
              textAlign: "center",
            }}
          >
            {translate("sidebar.noTeams")}
          </Box>
        ) : (
          <Droppable droppableId="sidebar-teams" type="PERSON" isDropDisabled>
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ display: "flex", flexDirection: "column", gap: "4px" /* spec: team row gap */ }}
              >
                {teams.map((team, idx) => {
                  const members = teamWorkloadMap[team.id] || [];
                  return (
                    <SidebarTeamRow
                      key={team.id}
                      teamId={team.id}
                      teamName={team.name}
                      memberCount={team.memberCount || members.length}
                      members={members}
                      draggableIndex={idx}
                    />
                  );
                })}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        )}
      </Box>

      {/* Drag hint — spec: Sora 11px/400, #8C9099, centered */}
      <Box
        sx={{
          padding: "4px 16px",
          fontFamily: dt.font,
          fontSize: "11px",
          fontWeight: 400,
          color: "#8C9099", /* spec: text-muted */
          textAlign: "center",
          flexShrink: 0,
        }}
      >
        {translate("sidebar.dragToAssign")}
      </Box>
    </Box>
  );
};

export default TeamSidebar;
